import { Dispatch } from "redux";
import { triggerNotification, NotificationData } from "@store/slices/notification";

interface NotificationDebouncer {
    contentHash: string;
    lastTriggered: number;
    count: number;
}

// Global debouncer state
const recentNotifications = new Map<string, NotificationDebouncer>();

/**
 * Smart notification trigger with debouncing to prevent spam
 * @param notification The notification to potentially trigger
 * @param dispatch Redux dispatch function
 * @param debounceWindowMs Debounce window in milliseconds (default: 3000ms)
 * @returns true if notification was sent, false if debounced
 */
export const smartTriggerNotification = (
    notification: NotificationData, 
    dispatch: Dispatch,
    debounceWindowMs: number = 3000
): boolean => {
    const contentHash = `${notification.type}_${notification.title || ''}_${notification.content}`;
    const now = Date.now();
    const existing = recentNotifications.get(contentHash);
    
    // Check if we should debounce this notification
    if (existing && (now - existing.lastTriggered) < debounceWindowMs) {
        console.log(`🔇 Debouncing duplicate notification (${existing.count + 1}x): ${contentHash}`);
        
        // Update count but don't trigger
        recentNotifications.set(contentHash, {
            ...existing,
            count: existing.count + 1
        });
        
        return false; // Notification blocked
    }
    
    // Update tracking
    recentNotifications.set(contentHash, {
        contentHash,
        lastTriggered: now,
        count: (existing?.count || 0) + 1
    });
    
    // Clean old entries (older than 10 seconds)
    cleanupOldNotifications(now);
    
    console.log(`📢 Triggering notification: ${contentHash}`);
    dispatch(triggerNotification(notification));
    return true; // Notification sent
};

/**
 * Reset debouncer for a specific notification type (useful after successful actions)
 */
export const resetNotificationDebouncer = (type: string, title?: string, content?: string) => {
    const contentHash = `${type}_${title || ''}_${content || ''}`;
    recentNotifications.delete(contentHash);
    console.log(`🔄 Reset notification debouncer: ${contentHash}`);
};

/**
 * Reset all auth-related notification debouncers (useful after successful logout)
 */
export const resetAuthNotificationDebouncers = () => {
    const authPatterns = ['session', 'auth', 'login', 'logout', 'expired', 'unauthorized'];
    let resetCount = 0;
    
    for (const [key, value] of recentNotifications.entries()) {
        const hasAuthPattern = authPatterns.some(pattern => 
            key.toLowerCase().includes(pattern)
        );
        
        if (hasAuthPattern) {
            recentNotifications.delete(key);
            resetCount++;
        }
    }
    
    if (resetCount > 0) {
        console.log(`🔄 Reset ${resetCount} auth notification debouncers`);
    }
};

/**
 * Clean up old notification entries
 */
const cleanupOldNotifications = (currentTime: number, maxAgeMs: number = 10000) => {
    let cleanedCount = 0;
    
    for (const [key, value] of recentNotifications.entries()) {
        if (currentTime - value.lastTriggered > maxAgeMs) {
            recentNotifications.delete(key);
            cleanedCount++;
        }
    }
    
    if (cleanedCount > 0) {
        console.log(`🧹 Cleaned up ${cleanedCount} old notification entries`);
    }
};

/**
 * Get debouncer statistics (useful for debugging)
 */
export const getNotificationDebounceStats = () => {
    return {
        totalEntries: recentNotifications.size,
        entries: Array.from(recentNotifications.entries()).map(([key, value]) => ({
            key,
            ...value,
            ageMs: Date.now() - value.lastTriggered
        }))
    };
};
